import { createElement, useCallback, useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import type { DataTableColumn } from '@/shared/components/data-table';
import { ReceivingStatusBadge } from '../components/receiving/ReceivingStatusBadge';
import { formatDate } from '../utils/timezone';
import type { ReceivingOrder, ReceivingOrderStatus } from '../types';

export interface ReceivingRenderContext {}

export type ReceivingColumnDef = DataTableColumn<ReceivingOrder, ReceivingRenderContext> & {
  defaultVisible?: boolean;
};

const ALL_COLUMN_KEYS = [
  'order_number', 'po_number', 'description', 'supplier_name', 'carrier',
  'expected_date', 'received_date', 'status', 'progress',
  'tracking_number', 'total_cost', 'requester_name',
  'supplier_order_number', 'payment_method', 'purchase_url',
  'created_by_name', 'notes', 'actions',
];
const HIDDEN_BY_DEFAULT = new Set<string>([
  'received_date', 'requester_name', 'supplier_order_number',
  'payment_method', 'purchase_url', 'created_by_name', 'notes',
]);

const VISIBILITY_KEY = 'atek-wms-receiving-column-visibility-v2';
const FILTERS_KEY = 'atek-wms-receiving-column-filters-v2';

const STATUS_OPTIONS: { label: string; value: ReceivingOrderStatus }[] = [
  { label: 'Draft', value: 'draft' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Pending QC', value: 'pending_qc' },
  { label: 'Completed', value: 'completed' },
  { label: 'Cancelled', value: 'cancelled' },
];

function getProgress(o: ReceivingOrder): number {
  const total = o.total_lines ?? o.line_count ?? 0;
  const received = o.received_lines ?? o.lines_complete ?? 0;
  if (!total) return 0;
  return Math.round((received / total) * 100);
}

function defaultVisibility(): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const k of ALL_COLUMN_KEYS) out[k] = !HIDDEN_BY_DEFAULT.has(k);
  return out;
}

function loadVisibility(): Record<string, boolean> {
  try {
    const s = localStorage.getItem(VISIBILITY_KEY);
    if (s) return { ...defaultVisibility(), ...JSON.parse(s) };
  } catch { /* ignore */ }
  return defaultVisibility();
}

function loadFilters(): Record<string, string> {
  try { return JSON.parse(localStorage.getItem(FILTERS_KEY) || '{}'); }
  catch { return {}; }
}

function buildColumns(): ReceivingColumnDef[] {
  return [
    {
      key: 'order_number',
      label: 'Order #',
      sortKey: 'orderNumber',
      filterType: 'text',
      isPrimarySearch: true,
      searchPlaceholder: 'Search order...',
      getValue: (o) => o.order_number,
      render: (o) => createElement('span', { className: 'font-mono font-medium' }, o.order_number),
    },
    {
      key: 'po_number',
      label: 'PO Number',
      sortKey: 'poNumber',
      filterType: 'text',
      getValue: (o) => o.po_number || '',
      render: (o) => createElement('span', { className: 'text-sm' }, o.po_number || '—'),
    },
    {
      key: 'supplier_name',
      label: 'Supplier',
      sortKey: 'supplierName',
      filterType: 'text',
      getValue: (o) => o.supplier_name || '',
      render: (o) => createElement('span', { className: 'text-sm' }, o.supplier_name || '—'),
    },
    {
      key: 'expected_date',
      label: 'Expected',
      sortKey: 'expectedDate',
      filterType: 'text',
      getValue: (o) => o.expected_date || '',
      render: (o) => createElement('span', { className: 'text-sm' }, o.expected_date ? formatDate(o.expected_date) : '—'),
    },
    {
      key: 'status',
      label: 'Status',
      sortKey: 'status',
      filterType: 'select',
      filterOptions: STATUS_OPTIONS,
      filterAllLabel: 'All Status',
      getValue: (o) => o.status,
      render: (o) => createElement(ReceivingStatusBadge, { status: o.status }),
    },
    {
      key: 'progress',
      label: 'Progress',
      noFilter: true,
      getValue: (o) => String(getProgress(o)),
      render: (o) => {
        const p = getProgress(o);
        const total = o.total_lines ?? o.line_count ?? 0;
        const received = o.received_lines ?? o.lines_complete ?? 0;
        return createElement(
          'div',
          { className: 'flex items-center gap-2 min-w-[120px]' },
          createElement('div', { className: 'flex-1 h-2 bg-gray-200 rounded-full overflow-hidden' },
            createElement('div', { className: 'h-full bg-green-500 transition-all', style: { width: `${p}%` } })),
          createElement('span', { className: 'text-xs text-muted-foreground whitespace-nowrap tabular-nums' },
            `${received}/${total}`),
        );
      },
    },
    {
      key: 'description',
      label: 'Description',
      filterType: 'text',
      getValue: (o) => o.description || '',
      render: (o) => createElement(
        'span',
        { className: 'text-sm line-clamp-1', title: o.description || '' },
        o.description || createElement('span', { className: 'text-muted-foreground' }, '—'),
      ),
    },
    {
      key: 'carrier',
      label: 'Carrier',
      filterType: 'text',
      getValue: (o) => o.carrier || '',
      render: (o) => o.carrier
        ? createElement('span', { className: 'text-sm' }, o.carrier)
        : createElement('span', { className: 'text-sm text-muted-foreground' }, '—'),
    },
    {
      key: 'received_date',
      label: 'Received',
      filterType: 'text',
      getValue: (o) => o.received_date || '',
      render: (o) => createElement('span', { className: 'text-sm' }, o.received_date ? formatDate(o.received_date) : '—'),
    },
    {
      key: 'tracking_number',
      label: 'Tracking',
      filterType: 'text',
      getValue: (o) => o.tracking_number || '',
      render: (o) => o.tracking_number
        ? createElement('span', { className: 'text-sm font-mono', title: o.tracking_number }, o.tracking_number)
        : createElement('span', { className: 'text-sm text-muted-foreground' }, '—'),
    },
    {
      key: 'requester_name',
      label: 'Requester',
      filterType: 'text',
      getValue: (o) => o.requester_name || '',
      render: (o) => createElement('span', { className: 'text-sm text-muted-foreground' }, o.requester_name || '—'),
    },
    {
      key: 'supplier_order_number',
      label: 'Supplier Order #',
      filterType: 'text',
      getValue: (o) => o.supplier_order_number || '',
      render: (o) => createElement('span', { className: 'text-sm font-mono text-muted-foreground' }, o.supplier_order_number || '—'),
    },
    {
      key: 'payment_method',
      label: 'Payment',
      filterType: 'text',
      getValue: (o) => o.payment_method || '',
      render: (o) => createElement('span', { className: 'text-sm text-muted-foreground' }, o.payment_method || '—'),
    },
    {
      key: 'purchase_url',
      label: 'URL',
      noFilter: true,
      getValue: (o) => o.purchase_url || '',
      render: (o) => o.purchase_url
        ? createElement(
            'a',
            {
              href: o.purchase_url,
              target: '_blank',
              rel: 'noopener noreferrer',
              className: 'inline-flex items-center gap-1 text-blue-600 hover:underline text-sm',
              onClick: (e: React.MouseEvent) => e.stopPropagation(),
            },
            'Link',
          )
        : createElement('span', { className: 'text-sm text-muted-foreground' }, '—'),
    },
    {
      key: 'total_cost',
      label: 'Price',
      sortKey: 'total_cost',
      noFilter: true,
      getValue: (o) => String(o.total_cost ?? 0),
      render: (o) => {
        const v = Number(o.total_cost ?? 0);
        if (!v) return createElement('span', { className: 'text-sm text-muted-foreground' }, '—');
        return createElement(
          'span',
          { className: 'text-sm tabular-nums font-medium' },
          v.toLocaleString('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2 }),
        );
      },
    },
    {
      key: 'created_by_name',
      label: 'Created By',
      filterType: 'text',
      getValue: (o) => o.created_by_name || '',
      render: (o) => createElement('span', { className: 'text-sm text-muted-foreground' }, o.created_by_name || '—'),
      defaultVisible: false,
    },
    {
      key: 'notes',
      label: 'Notes',
      filterType: 'text',
      getValue: (o) => o.notes || '',
      render: (o) => createElement(
        'span',
        { className: 'text-sm text-muted-foreground line-clamp-1', title: o.notes || '' },
        o.notes || '—',
      ),
      defaultVisible: false,
    },
    {
      key: 'actions',
      label: '',
      noFilter: true,
      getValue: () => '',
      render: () => createElement(ChevronRight, { className: 'h-4 w-4 text-gray-400' }),
    },
  ];
}

function escapeCSV(v: unknown): string {
  if (v === null || v === undefined) return '';
  let s = String(v);
  if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
  if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function exportReceivingCSV(items: ReceivingOrder[], cols: ReceivingColumnDef[], ctx: ReceivingRenderContext) {
  const exp = cols.filter((c) => c.label);
  const header = exp.map((c) => escapeCSV(c.label)).join(',');
  const rows = items.map((it) => exp.map((c) => escapeCSV(c.getValue(it, ctx))).join(','));
  const csv = [header, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `receiving_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function copyReceivingToClipboard(items: ReceivingOrder[], cols: ReceivingColumnDef[], ctx: ReceivingRenderContext) {
  const exp = cols.filter((c) => c.label);
  const header = exp.map((c) => c.label).join('\t');
  const rows = items.map((it) => exp.map((c) => String(c.getValue(it, ctx) ?? '')).join('\t'));
  return navigator.clipboard.writeText([header, ...rows].join('\n'));
}

export interface UseReceivingListTableResult {
  sortKey: string;
  sortDirection: 1 | -1;
  columnFilters: Record<string, string>;
  columnVisibility: Record<string, boolean>;
  columns: ReceivingColumnDef[];
  visibleColumns: ReceivingColumnDef[];
  ctx: ReceivingRenderContext;
  sortedItems: ReceivingOrder[];
  totalCount: number;
  isVisible: (key: string) => boolean;
  toggleColumn: (key: string) => void;
  setColumnFilter: (key: string, value: string) => void;
  handleSort: (key: string) => void;
}

export function useReceivingListTable(items: ReceivingOrder[]): UseReceivingListTableResult {
  const [sortKey, setSortKey] = useState<string>('orderNumber');
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(loadFilters);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(loadVisibility);

  const ctx: ReceivingRenderContext = useMemo(() => ({}), []);
  const columns = useMemo(() => buildColumns(), []);

  const isVisible = useCallback(
    (key: string) => columnVisibility[key] !== false,
    [columnVisibility],
  );

  const visibleColumns = useMemo(
    () => columns.filter((c) => columnVisibility[c.key] !== false),
    [columns, columnVisibility],
  );

  const toggleColumn = useCallback((key: string) => {
    setColumnVisibility((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem(VISIBILITY_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const setColumnFilter = useCallback((key: string, value: string) => {
    setColumnFilters((prev) => {
      const next = { ...prev, [key]: value };
      localStorage.setItem(FILTERS_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const filtered = useMemo(() => {
    const active = Object.entries(columnFilters).filter(([, v]) => v);
    if (active.length === 0) return items;
    return items.filter((it) => active.every(([k, fv]) => {
      const col = columns.find((c) => c.key === k);
      if (!col || col.noFilter) return true;
      const raw = col.getValue(it, ctx);
      if (col.filterType === 'select') {
        try {
          const sel = JSON.parse(fv) as string[];
          return sel.length === 0 || sel.includes(String(raw || ''));
        } catch { return true; }
      }
      return String(raw ?? '').toLowerCase().includes(fv.toLowerCase());
    }));
  }, [items, columnFilters, columns, ctx]);

  const sortedItems = useMemo(() => {
    const col = columns.find((c) => c.sortKey === sortKey);
    if (!col) return filtered;
    return [...filtered].sort((a, b) => {
      const aRaw = col.getValue(a, ctx);
      const bRaw = col.getValue(b, ctx);
      if (sortKey === 'total_cost') {
        return (Number(aRaw) - Number(bRaw)) * sortDirection;
      }
      return String(aRaw ?? '').localeCompare(String(bRaw ?? ''), undefined, { sensitivity: 'base' }) * sortDirection;
    });
  }, [filtered, sortKey, sortDirection, columns, ctx]);

  const handleSort = useCallback((key: string) => {
    if (sortKey === key) setSortDirection((d) => (d === 1 ? -1 : 1));
    else { setSortKey(key); setSortDirection(1); }
  }, [sortKey]);

  return {
    sortKey, sortDirection, columnFilters, columnVisibility,
    columns, visibleColumns, ctx, sortedItems,
    totalCount: items.length,
    isVisible, toggleColumn, setColumnFilter, handleSort,
  };
}
